import { C as Cart } from './cart-DZS7CGyq.js';
import { P as PubSub } from './pubsub-COBwjfLh.js';
import './uniq-BDEfcc0N.js';
import './notyf-DIw0RvBj.js';

/**
 * Confirmation banner when a cart-drawer recommendation is added.
 *
 * @element recommend-mini-cart
 */


const NOTIFICATION_ID = 'recommend-mini-cart';
const RECOMMEND_MINI_CART_SOURCE = 'algolia-recommend-mini-cart';

/**
 * @param {string} title
 * @returns {{ genus: string, cultivar: string }}
 */
function splitProductTitle(title) {
  const apostropheIndex = title.indexOf("'");
  if (apostropheIndex === -1) return { genus: title, cultivar: '' };

  return {
    genus: title.slice(0, apostropheIndex).trim(),
    cultivar: title.slice(apostropheIndex).trim(),
  };
}

/**
 * @param {object} item
 * @returns {string}
 */
function getItemImageUrl(item) {
  if (typeof item?.image === 'string' && item.image) return item.image;
  if (typeof item?.featured_image === 'string' && item.featured_image) return item.featured_image;

  return item?.featured_image?.url || item?.featured_image?.src || '';
}

/**
 * @param {object} item
 * @returns {string}
 */
function getItemTitle(item) {
  return item?.product_title || item?.title || '';
}

class RecommendMiniCart extends HTMLElement {
  #timeout;
  #item;
  #unsubscribers = [];
  #drawerObserver;
  #titleEl;
  #imageEl;
  #cancelButton;
  #onCancelClick = (event) => {
    event.preventDefault();
    this.#cancel();
  };

  connectedCallback() {
    const existingOnBody = [...document.querySelectorAll('recommend-mini-cart')].find(
      (element) => element !== this && element.parentElement === document.body
    );

    if (existingOnBody) {
      this.remove();
      return;
    }

    if (this.parentElement !== document.body) {
      document.body.append(this);
      return;
    }

    this.#titleEl = this.querySelector('[data-recommend-mini-cart-title]');
    this.#imageEl = this.querySelector('[data-recommend-mini-cart-image]');
    this.#cancelButton = this.querySelector('[data-recommend-mini-cart-cancel]');

    this.#unsubscribers.push(
      PubSub.subscribe('notification:open', (payload) => this.#onOpen(payload))
    );

    this.#cancelButton?.addEventListener('click', this.#onCancelClick);
    this.#observeCartDrawer();
  }

  disconnectedCallback() {
    this.#unsubscribers.forEach((unsubscribe) => unsubscribe());
    this.#unsubscribers = [];
    this.#cancelButton?.removeEventListener('click', this.#onCancelClick);
    this.#drawerObserver?.disconnect();
    clearTimeout(this.#timeout);
  }

  close() {
    clearTimeout(this.#timeout);
    this.dataset.open = 'false';
    this.ariaHidden = 'true';
    this.#item = undefined;
  }

  #onOpen(payload) {
    if (payload?.id !== this.id && payload?.id !== NOTIFICATION_ID) return;

    const item = Array.isArray(payload.items) ? payload.items[0] : payload.items;
    if (!item) return;

    this.#item = item;
    this.#paint(item);
    this.dataset.open = 'true';
    this.ariaHidden = 'false';
    this.#startTimer();
  }

  #paint(item) {
    const title = getItemTitle(item);
    const { genus, cultivar } = splitProductTitle(title);
    const imageUrl = getItemImageUrl(item);

    this.#paintTitle(genus, cultivar);
    this.#paintImage(title, imageUrl);
  }

  #paintTitle(genus, cultivar) {
    if (!this.#titleEl) return;

    this.#titleEl.replaceChildren();

    if (genus) {
      const genusEl = document.createElement('em');
      genusEl.className = 'italic';
      genusEl.textContent = genus;
      this.#titleEl.append(genusEl);
    }

    if (cultivar) {
      const cultivarEl = document.createElement('span');
      cultivarEl.className = 'font-mono';
      cultivarEl.textContent = ` ${cultivar}`;
      this.#titleEl.append(cultivarEl);
    }
  }

  #paintImage(title, imageUrl) {
    if (!this.#imageEl) return;

    if (!imageUrl) {
      this.#imageEl.hidden = true;
      this.#imageEl.removeAttribute('src');
      return;
    }

    this.#imageEl.hidden = false;
    this.#imageEl.alt = title;
    this.#imageEl.src = imageUrl;
  }

  #startTimer() {
    clearTimeout(this.#timeout);
    const duration = Number(this.dataset.durationMs) || 4000;
    this.#timeout = setTimeout(() => this.close(), duration);
  }

  async #cancel() {
    const key = this.#item?.key;
    const quantity = Math.max(0, Number(this.#item?.quantity) - 1);

    this.close();
    if (!key) return;

    try {
      await Cart.change({
        id: key,
        quantity,
        event_data: { source: RECOMMEND_MINI_CART_SOURCE },
      });
    } catch {
      // The item may already have been removed; the banner is already closed.
    }
  }

  #observeCartDrawer() {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;

    this.#drawerObserver = new MutationObserver(() => {
      if (cartDrawer.getAttribute('aria-hidden') !== 'false') this.close();
    });
    this.#drawerObserver.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
    });
  }
}

customElements.define('recommend-mini-cart', RecommendMiniCart);
